import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { AvailabilityEffect, CreateAvailabilityRuleRequest } from '../types';
import {
  browserTimezone,
  buildStartTimeIso,
  buildWeeklyRRule,
  durationFromTimes,
  WEEKDAYS,
} from '../utils/schedule';

interface AvailabilityRuleFormProps {
  onCreate: (payload: CreateAvailabilityRuleRequest) => void;
  isSubmitting?: boolean;
}

/** Presentational: add a recurring weekly availability window (open or block).
 * One-off windows / per-encounter-type scoping are a fast-follow. */
export function AvailabilityRuleForm({
  onCreate,
  isSubmitting = false,
}: AvailabilityRuleFormProps) {
  const [effect, setEffect] = useState<AvailabilityEffect>('open');
  const [days, setDays] = useState<string[]>(['MO', 'TU', 'WE', 'TH', 'FR']);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');

  const duration = durationFromTimes(startTime, endTime);
  const valid = days.length > 0 && duration > 0;
  const helperText = useMemo(() => {
    if (days.length === 0) return 'Pick at least one day.';
    if (duration <= 0) return 'End time must be after start time.';
    return ' ';
  }, [days.length, duration]);

  const handleSubmit = () => {
    if (!valid) return;
    onCreate({
      effect,
      rrule: buildWeeklyRRule(days),
      start_time: buildStartTimeIso(startTime),
      duration_minutes: duration,
      timezone: browserTimezone(),
    });
  };

  return (
    <Box
      component="form"
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
      sx={{ mt: 2 }}
      aria-label="Add availability window"
    >
      <Stack spacing={1.5}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={effect}
          onChange={(_event, value) => value && setEffect(value)}
          aria-label="Window type"
        >
          <ToggleButton value="open">Open hours</ToggleButton>
          <ToggleButton value="block">Block</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          value={days}
          onChange={(_event, value: string[]) => setDays(value)}
          aria-label="Days of week"
        >
          {WEEKDAYS.map(day => (
            <ToggleButton key={day.code} value={day.code} aria-label={day.label}>
              {day.short}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
        >
          <TextField
            type="time"
            size="small"
            label="Start"
            value={startTime}
            onChange={event => setStartTime(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="time"
            size="small"
            label="End"
            value={endTime}
            onChange={event => setEndTime(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            error={duration <= 0}
          />
          <Button
            type="submit"
            variant="contained"
            size="medium"
            disabled={!valid || isSubmitting}
          >
            Add window
          </Button>
        </Stack>
        <Typography
          variant="caption"
          color={valid ? 'text.secondary' : 'error'}
          sx={{ minHeight: 18 }}
        >
          {valid ? `Times are in ${browserTimezone()}.` : helperText}
        </Typography>
      </Stack>
    </Box>
  );
}
